import React from 'react';
import { pdf } from '@react-pdf/renderer';

export const PdfService = {
    /**
     * Converts a generic image URL (including SVGs) into a Base64 string via Canvas
     * so it can be safely embedded into `@react-pdf/renderer`.
     */
    getBase64ImageFromUrl: async (imageUrl: string, format = 'image/jpeg'): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined') {
                return reject(new Error('Cannot convert image to base64 outside browser'));
            }

            const img = new window.Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL(format, 0.9));
                } else {
                    reject(new Error('Failed to get canvas context'));
                }
            };
            img.onerror = (e) => reject(e);
            img.src = imageUrl;
        });
    },

    /**
     * Automatically fetches the Storyverse SVG logo as a base64 string.
     */
    fetchBrandLogoBase64: async (): Promise<string> => {
        try {
            return await PdfService.getBase64ImageFromUrl('/storyverse.svg', 'image/png');
        } catch (logoErr) {
            console.warn('Failed to fetch brand logo for PDF', logoErr);
            return '';
        }
    },

    /**
     * Renders a `@react-pdf/renderer` Document element into a Blob, creates an Object URL, 
     * triggers the browser's download dialog, and cleans up memory.
     */
    downloadReactPdfDocument: async (documentElement: React.ReactElement<any>, fileName: string) => {
        if (typeof window === 'undefined') return;

        try {
            console.log(`Starting generation of React-PDF: ${fileName}`);
            const blob = await pdf(documentElement).toBlob();
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.pdf`;
            
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            console.log(`React-PDF "${fileName}" downloaded successfully rendered entirely in-memory.`);
        } catch (error) {
            console.error('Error generating React-PDF Document:', error);
            throw error; // Re-throw to allow component-level error handling (e.g., throwing toasts)
        }
    }
};
